import { CSSProperties, FormEventHandler, ReactNode } from 'react';
import { Language } from '@/api/types';
import {
  useCollapsingHeader,
  type HeroLock,
} from '@/hooks/useCollapsingHeader';
import { actionColor, brandStateVars, contrastText } from '@/lib/brandColor';
import { BrandHero } from './BrandHero';
import { ApplyFormSheet } from './ApplyFormSheet';
import { NoDataState } from './NoDataState';
import PoweredBy from '../PoweredBy';

interface ApplyFlowShellProps {
  bgColor: string;
  inkColor: string;
  logoUrl: string;
  brandName?: string;
  welcomeTitle: ReactNode;
  welcomeMessage?: ReactNode;
  language: Language;
  availableLanguages: Language[];
  onLanguageChange: (language: Language) => void;
  footer: ReactNode;
  onSubmit?: FormEventHandler<HTMLFormElement>;
  noData?: { title: string; body: string };
  /**
   * Lock the brand hero at a fixed height instead of collapsing on scroll.
   * `'full'` (260px) · `'compact'` (64px) · a specific pixel height. Omit for
   * the default collapse-on-scroll behavior.
   */
  lockHero?: HeroLock;
  children: ReactNode;
}

const cardClass =
  'apply-flow-shell relative h-[100dvh] w-full overflow-hidden bg-white';

export function ApplyFlowShell({
  bgColor,
  inkColor,
  logoUrl,
  brandName,
  welcomeTitle,
  welcomeMessage,
  language,
  availableLanguages,
  onLanguageChange,
  footer,
  onSubmit,
  noData,
  lockHero,
  children,
}: ApplyFlowShellProps) {
  const { scrollRef, cardRef, sizerRef, onScroll } =
    useCollapsingHeader(lockHero);

  // Accent colour for the CTA/steps/no-data — the brand colour unless it's
  // white/near-white, in which case the app primary (so it stays visible on the
  // white sheet). The hero background keeps the real `bgColor`.
  const action = actionColor(bgColor);

  const style = {
    '--bg-color': action,
    // CTA label colour is computed from the CTA's own background (the action
    // colour), so it stays legible regardless of the API `text_color`.
    '--text-color': contrastText(action),
    '--destructive': '0 90% 50%',
    ...brandStateVars(action),
  } as CSSProperties;

  // Unbranded no-data (API error / missing details): no brand to build a hero
  // from, so keep the bare centered message.
  if (noData && bgColor === 'inherit') {
    return (
      <div
        className="grid min-h-[100dvh] place-items-center bg-[#f4f4f5]"
        style={style}
      >
        <div className={cardClass}>
          <NoDataState
            title={noData.title}
            body={noData.body}
            brandColor={action}
            logoUrl={logoUrl || undefined}
          />
        </div>
      </div>
    );
  }

  // Branded no-data: keep the hero section (logo on the brand colour), put the
  // message where the form/list would go, and drop the CTA (nothing to submit).
  // The hero is static-expanded and its welcome copy is suppressed so it reads
  // as branding, not a "choose a position" prompt that contradicts the message.
  if (noData) {
    return (
      <div
        className="grid min-h-[100dvh] place-items-center bg-[#f4f4f5]"
        style={style}
      >
        <div className={cardClass}>
          <div className="flex h-full flex-col">
            <div
              className="shrink-0"
              style={{ height: 'var(--hero-expanded)' }}
              aria-hidden
            />
            <div className="min-h-0 flex-1">
              <NoDataState
                title={noData.title}
                body={noData.body}
                brandColor={action}
              />
            </div>
            <div className="shrink-0 border-t border-[#eef0f2] bg-white px-5 pb-5 pt-3">
              <PoweredBy className="mt-2.5" />
            </div>
          </div>
          <BrandHero
            bgColor={bgColor}
            inkColor={inkColor}
            logoUrl={logoUrl}
            brandName={brandName}
            welcomeTitle=""
            language={language}
            availableLanguages={availableLanguages}
            onLanguageChange={onLanguageChange}
          />
        </div>
      </div>
    );
  }

  const fill = (
    <>
      {/* scroll area */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="min-h-0 flex-1 overflow-y-auto"
      >
        {/* collapse-room: extra scroll room that drives the collapse. Pure CSS —
            equals the responsive expanded height minus the compact bar. */}
        <div
          className="flex flex-col"
          style={{
            // Any lock pins the hero, so there is no collapse to leave room
            // for. Only the unlocked (collapse-on-scroll) case needs it.
            minHeight:
              lockHero != null
                ? 'auto'
                : 'calc(100% + var(--collapse-distance))',
          }}
        >
          {/* Spacer reserves the hero height the content must clear. It follows
              the lock: a compact-locked hero is only --hero-compact tall, and
              reserving the expanded height there leaves a band of dead space.
              Its measured height also feeds the hook's scroll→progress mapping
              via ResizeObserver — safe here because `lockProgress` hardcodes
              0/1 for 'full'/'compact' and ignores the measurement. A numeric
              lock does use it, so that case would need measuring split from
              spacing before it could shrink. */}
          <div
            ref={sizerRef}
            className="shrink-0"
            style={{
              height:
                lockHero === 'compact'
                  ? 'var(--hero-compact)'
                  : 'var(--hero-expanded)',
            }}
            aria-hidden
          />
          <ApplyFormSheet className="flex-1">{children}</ApplyFormSheet>
        </div>
      </div>
      {/* pinned footer */}
      <div className="shrink-0 border-t border-[#eef0f2] bg-white px-5 pb-5 pt-3 text-center short:pb-4 short:pt-2">
        {footer}
        <PoweredBy className="mt-2.5 short:mt-1" />
      </div>
    </>
  );

  return (
    <div
      className="grid min-h-[100dvh] place-items-center bg-[#f4f4f5]"
      style={style}
    >
      <div ref={cardRef} className={cardClass}>
        {onSubmit ? (
          <form onSubmit={onSubmit} className="flex h-full flex-col">
            {fill}
          </form>
        ) : (
          <div className="flex h-full flex-col">{fill}</div>
        )}

        <BrandHero
          bgColor={bgColor}
          inkColor={inkColor}
          logoUrl={logoUrl}
          brandName={brandName}
          welcomeTitle={welcomeTitle}
          welcomeMessage={welcomeMessage}
          language={language}
          availableLanguages={availableLanguages}
          onLanguageChange={onLanguageChange}
        />
      </div>
    </div>
  );
}
